import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import OrgTreeSelect from '@/components/common/OrgTreeSelect';
import type { StatusFilter, ConnectedFilter, PresetFilter } from '@/types/fleet';

interface FleetFilterSelectChipProps {
  label: string;
  value: string;
  isActive: boolean;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FleetFilterSelectChip: React.FC<FleetFilterSelectChipProps> = ({
  label,
  value,
  isActive,
  onChange,
  options,
}) => {
  const selectedLabel = options.find((o) => o.value === value)?.label ?? options[0]?.label ?? '';

  return (
    <div className="relative inline-flex items-center min-w-[11rem]">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className={`w-full min-w-[11rem] appearance-none cursor-pointer pl-3 pr-8 py-1.5 text-sm rounded-md border transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/40 text-transparent ${
          isActive
            ? 'border-teal-500 bg-teal-50/60 dark:bg-teal-900/20'
            : 'border-border bg-bgContainer hover:border-border/80'
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="text-textDefault">
            {o.label}
          </option>
        ))}
      </select>
      <span
        className="pointer-events-none absolute inset-y-0 left-3 right-7 flex items-center gap-1.5 text-sm truncate"
        aria-hidden
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted shrink-0">
          {label}
        </span>
        <span className="text-text-muted shrink-0">|</span>
        <span className={`truncate ${isActive ? 'font-medium text-teal-700 dark:text-teal-300' : 'text-textDefault'}`}>
          {selectedLabel}
        </span>
      </span>
      <ChevronDownIcon className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
    </div>
  );
};

export interface FleetOverviewHeaderProps {
  t: (key: string) => string;
  searchInput: string;
  setSearchInput: (value: string) => void;
  search: string;
  onSearch: (e: React.FormEvent) => void;
  onShowGuide: () => void;
  canAddVehicle: boolean;
  onAddVehicle: () => void;
  onOpenColumnSettings: () => void;
  onOpenCarInfoFilter: () => void;
  carInfoFilterIds: number[] | null;
  exportRegisterLoading: boolean;
  onExportVehicleRegister: (format: 'xlsx' | 'csv') => void;
  onExportFleetReport: (format: 'xlsx' | 'csv') => void;
  organizationFilter: string;
  onOrganizationFilterChange: (orgId: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (value: StatusFilter) => void;
  connectedFilter: ConnectedFilter;
  setConnectedFilter: (value: ConnectedFilter) => void;
  presetFilter: PresetFilter;
  setPresetFilter: (value: PresetFilter) => void;
  usageStatusFilter: string;
  setUsageStatusFilter: (value: string) => void;
  usageStatusOptions: { id: number; name: string }[];
  onClearFilters: () => void;
  vehicleCount: number | null;
  columnFiltersEnabled: boolean;
  onToggleColumnFilters: () => void;
}

const FleetOverviewHeader: React.FC<FleetOverviewHeaderProps> = ({
  t,
  searchInput,
  setSearchInput,
  search,
  onSearch,
  onShowGuide,
  canAddVehicle,
  onAddVehicle,
  onOpenColumnSettings,
  onOpenCarInfoFilter,
  carInfoFilterIds,
  exportRegisterLoading,
  onExportVehicleRegister,
  onExportFleetReport,
  organizationFilter,
  onOrganizationFilterChange,
  statusFilter,
  setStatusFilter,
  connectedFilter,
  setConnectedFilter,
  presetFilter,
  setPresetFilter,
  usageStatusFilter,
  setUsageStatusFilter,
  usageStatusOptions,
  onClearFilters,
  vehicleCount,
  columnFiltersEnabled,
  onToggleColumnFilters,
}) => {
  const navigate = useNavigate();
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exportOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (exportRef.current && !exportRef.current.contains(event.target as Node)) {
        setExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [exportOpen]);

  /** Antal val i filterraden som avviker från standard (0 vid sidladdning). */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (organizationFilter) count++;
    if (statusFilter !== 'ACTIVE') count++;
    if (connectedFilter) count++;
    if (presetFilter) count++;
    if (usageStatusFilter) count++;
    if (carInfoFilterIds !== null) count++;
    return count;
  }, [organizationFilter, statusFilter, connectedFilter, presetFilter, usageStatusFilter, carInfoFilterIds]);

  const hasNonDefaultFilters =
    organizationFilter !== '' ||
    statusFilter !== 'ACTIVE' ||
    connectedFilter !== '' ||
    presetFilter !== '' ||
    usageStatusFilter !== '' ||
    carInfoFilterIds !== null ||
    search !== '';

  const statusOptions = useMemo(
    () => [
      { value: 'ACTIVE', label: t('fleet.statusActive') || 'Aktiv' },
      { value: 'INACTIVE', label: t('fleet.statusInactive') || 'Inaktiv' },
      { value: '', label: t('fleet.filterAll') || 'Alla' },
    ],
    [t]
  );

  const connectedOptions = useMemo(
    () => [
      { value: '', label: t('fleet.filterAll') || 'Alla' },
      { value: 'connected', label: t('fleet.filterConnected') || 'Kopplade' },
      { value: 'disconnected', label: t('fleet.filterDisconnected') || 'Okopplade' },
    ],
    [t]
  );

  const presetOptions = useMemo(
    () => [
      { value: '', label: t('fleet.filterPresetAll') || 'Alla' },
      { value: 'poolcars', label: t('fleet.filterPoolcars') || 'Alla poolbilar' },
      { value: 'mil', label: t('fleet.filterMil') || 'Motorlampa lyser (MIL)' },
      { value: 'tax-due', label: t('fleet.filterTaxDue') },
      { value: 'inspection-due', label: t('fleet.filterInspectionDue') },
    ],
    [t]
  );

  const usageStatusSelectOptions = useMemo(
    () => [
      { value: '', label: t('fleet.filterAll') || 'Alla' },
      { value: 'none', label: t('fleet.noUsageStatus') || 'Ingen status' },
      ...usageStatusOptions.map((s) => ({ value: String(s.id), label: s.name })),
    ],
    [t, usageStatusOptions]
  );

  const toolbarBtnClass =
    'inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border bg-bgContainer text-textDefault hover:bg-bgHover transition-colors whitespace-nowrap';

  return (
    <div className="mb-4 rounded-xl border border-border bg-bgElevated shadow-sm p-4">
      {/* Rad 1: identitet · sök · åtgärder */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-4">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.SYSTEM_DEQUE)}
            className="inline-flex shrink-0 items-center justify-center p-2 rounded-lg border border-border bg-bgContainer hover:bg-bgHover transition-colors"
            title={t('fleet.backToDeque') || 'Tillbaka till Deque'}
            aria-label={t('fleet.backToDeque') || 'Tillbaka till Deque'}
          >
            <ArrowLeftIcon className="w-5 h-5 text-textDefault" aria-hidden />
          </button>
          <h1 className="text-xl font-bold text-textDefault whitespace-nowrap">{t('fleet.title')}</h1>
        </div>

        <form
          onSubmit={onSearch}
          className="flex flex-1 items-center gap-2 min-w-0 w-full lg:w-auto"
        >
          <div data-guide="fleet-search" className="relative flex-1 min-w-0">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('fleet.searchPlaceholderShort')}
              title={t('fleet.searchPlaceholder')}
              className="w-full pl-9 pr-10 py-2 text-sm border border-border rounded-lg bg-bgContainer text-textDefault placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500"
            />
            <button
              type="button"
              onClick={onShowGuide}
              title={t('guide.showGuide') || 'Visa guide'}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-teal-600 rounded transition-colors"
            >
              <span className="text-sm font-bold leading-none">?</span>
            </button>
          </div>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shrink-0"
          >
            {t('common.search') || 'Sök'}
          </button>
        </form>

        <div className="flex flex-wrap items-center gap-2 shrink-0 lg:ml-auto">
          <button
            type="button"
            data-guide="fleet-filter"
            onClick={onOpenCarInfoFilter}
            className={toolbarBtnClass}
          >
            <FunnelIcon className="h-4 w-4" />
            <span>{t('common.filter') || 'Filter'}</span>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-semibold rounded-full bg-green-600 text-white">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            type="button"
            data-guide="fleet-columns"
            onClick={onOpenColumnSettings}
            className={toolbarBtnClass}
            title={t('fleet.columnSettings.title')}
          >
            <ViewColumnsIcon className="h-4 w-4" />
            <span>{t('fleet.columnSettings.button')}</span>
          </button>

          <button
            type="button"
            onClick={onToggleColumnFilters}
            aria-pressed={columnFiltersEnabled}
            title={t('fleet.columnFilterToggleHint')}
            className={`${toolbarBtnClass} ${
              columnFiltersEnabled
                ? 'border-teal-500 bg-teal-50/60 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300'
                : ''
            }`}
          >
            <MagnifyingGlassIcon className="h-4 w-4" />
            <span>{t('fleet.columnFilterToggle')}</span>
          </button>

          <div ref={exportRef} className="relative">
            <button
              type="button"
              disabled={exportRegisterLoading}
              onClick={() => setExportOpen((o) => !o)}
              className={`${toolbarBtnClass} disabled:opacity-50`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>{t('common.export') || 'Exportera'}</span>
              <ChevronDownIcon className={`h-4 w-4 transition-transform ${exportOpen ? 'rotate-180' : ''}`} />
            </button>
            {exportOpen && (
              <div className="absolute right-0 z-50 mt-1 min-w-[220px] rounded-lg border border-border bg-bgElevated shadow-lg py-1">
                <button
                  type="button"
                  disabled={exportRegisterLoading}
                  onClick={() => {
                    onExportVehicleRegister('xlsx');
                    setExportOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-textDefault hover:bg-bgHover disabled:opacity-50"
                >
                  {t('fleet.exportVehicleRegister')} — {t('fleet.exportVehicleRegisterExcel')}
                </button>
                <button
                  type="button"
                  disabled={exportRegisterLoading}
                  onClick={() => {
                    onExportVehicleRegister('csv');
                    setExportOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-textDefault hover:bg-bgHover disabled:opacity-50"
                >
                  {t('fleet.exportVehicleRegister')} — {t('fleet.exportVehicleRegisterCsv')}
                </button>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  disabled={exportRegisterLoading}
                  onClick={() => {
                    onExportFleetReport('xlsx');
                    setExportOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-textDefault hover:bg-bgHover disabled:opacity-50"
                >
                  {t('fleet.exportFleetReport')} — {t('fleet.exportFleetReportExcel')}
                </button>
                <button
                  type="button"
                  disabled={exportRegisterLoading}
                  onClick={() => {
                    onExportFleetReport('csv');
                    setExportOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-textDefault hover:bg-bgHover disabled:opacity-50"
                >
                  {t('fleet.exportFleetReport')} — {t('fleet.exportFleetReportCsv')}
                </button>
              </div>
            )}
          </div>

          {canAddVehicle && (
            <button
              type="button"
              onClick={onAddVehicle}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shrink-0"
            >
              <PlusIcon className="h-4 w-4" />
              <span>{t('fleet.addVehicle.button') || 'Nytt fordon'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Rad 2: filter */}
      <div
        data-guide="fleet-org-select"
        className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted mr-1 shrink-0">
          {t('fleet.filterBarLabel')}
        </span>

        <OrgTreeSelect
          value={organizationFilter}
          onChange={onOrganizationFilterChange}
          showEmptyOption
          emptyLabel={t('fleet.filterAllOrganizations') || 'Alla organisationer'}
          variant="chip"
          chipLabel={t('fleet.organization') || 'Organisation'}
          isActive={organizationFilter !== ''}
          className="inline-block w-auto min-w-[180px]"
        />

        <FleetFilterSelectChip
          label={t('fleet.systemStatus') || 'Systemstatus'}
          value={statusFilter}
          isActive={statusFilter !== 'ACTIVE'}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          options={statusOptions}
        />

        <FleetFilterSelectChip
          label={t('fleet.connected') || 'Kopplad'}
          value={connectedFilter}
          isActive={connectedFilter !== ''}
          onChange={(v) => setConnectedFilter(v as ConnectedFilter)}
          options={connectedOptions}
        />

        <FleetFilterSelectChip
          label={t('fleet.filterPreset') || 'Filter'}
          value={presetFilter}
          isActive={presetFilter !== ''}
          onChange={(v) => setPresetFilter(v as PresetFilter)}
          options={presetOptions}
        />

        <FleetFilterSelectChip
          label={t('fleet.filterUsageStatus') || 'Status'}
          value={usageStatusFilter}
          isActive={usageStatusFilter !== ''}
          onChange={setUsageStatusFilter}
          options={usageStatusSelectOptions}
        />

        {hasNonDefaultFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-sm text-text-muted hover:text-textDefault px-1 transition-colors"
          >
            {t('common.clear') || 'Rensa'}
          </button>
        )}

        {vehicleCount != null && vehicleCount > 0 && (
          <div className="flex items-center gap-2 ml-auto text-sm text-text-muted shrink-0">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
            </span>
            <span>
              {t('fleet.showing') || 'Visar'}{' '}
              <span className="font-semibold text-textDefault">
                {vehicleCount}
              </span>{' '}
              {t('fleet.vehicles') || 'fordon'}
              {search && (
                <span className="font-normal text-text-muted ml-1">
                  ({t('common.search')}: &quot;{search}&quot;)
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FleetOverviewHeader;
